"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Save, X } from "lucide-react";
import { StandardFormSelect, StandardFormTagInput } from "@/components/common/StandardFormInput";

interface DeliveryExclusions {
  excludedStates: string[];
  excludedCities: string[];
  excludedPincodes: string[];
}

const DeliveryLocations = () => {
  const [exclusions, setExclusions] = useState<DeliveryExclusions>({
    excludedStates: [],
    excludedCities: [],
    excludedPincodes: [],
  });

  const [editExclusions, setEditExclusions] = useState<DeliveryExclusions>({
    excludedStates: [],
    excludedCities: [],
    excludedPincodes: [],
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Mock data - in real app this would come from API
  const states = [
    { label: "Andhra Pradesh", value: "andhra-pradesh" },
    { label: "Arunachal Pradesh", value: "arunachal-pradesh" },
    { label: "Assam", value: "assam" },
    { label: "Bihar", value: "bihar" },
    { label: "Chhattisgarh", value: "chhattisgarh" },
    { label: "Goa", value: "goa" },
    { label: "Gujarat", value: "gujarat" },
    { label: "Haryana", value: "haryana" },
    { label: "Himachal Pradesh", value: "himachal-pradesh" },
    { label: "Jharkhand", value: "jharkhand" },
    { label: "Karnataka", value: "karnataka" },
    { label: "Kerala", value: "kerala" },
    { label: "Madhya Pradesh", value: "madhya-pradesh" },
    { label: "Maharashtra", value: "maharashtra" },
    { label: "Manipur", value: "manipur" },
    { label: "Meghalaya", value: "meghalaya" },
    { label: "Mizoram", value: "mizoram" },
    { label: "Nagaland", value: "nagaland" },
    { label: "Odisha", value: "odisha" },
    { label: "Punjab", value: "punjab" },
    { label: "Rajasthan", value: "rajasthan" },
    { label: "Sikkim", value: "sikkim" },
    { label: "Tamil Nadu", value: "tamil-nadu" },
    { label: "Telangana", value: "telangana" },
    { label: "Tripura", value: "tripura" },
    { label: "Uttar Pradesh", value: "uttar-pradesh" },
    { label: "Uttarakhand", value: "uttarakhand" },
    { label: "West Bengal", value: "west-bengal" },
    { label: "Delhi", value: "delhi" },
    { label: "Jammu and Kashmir", value: "jammu-kashmir" },
    { label: "Ladakh", value: "ladakh" },
    { label: "Puducherry", value: "puducherry" },
  ];

  const cities = [
    { label: "Mumbai", value: "mumbai" },
    { label: "Delhi", value: "delhi" },
    { label: "Bangalore", value: "bangalore" },
    { label: "Hyderabad", value: "hyderabad" },
    { label: "Chennai", value: "chennai" },
    { label: "Kolkata", value: "kolkata" },
    { label: "Pune", value: "pune" },
    { label: "Jaipur", value: "jaipur" },
    { label: "Surat", value: "surat" },
    { label: "Lucknow", value: "lucknow" },
    { label: "Kanpur", value: "kanpur" },
    { label: "Nagpur", value: "nagpur" },
    { label: "Indore", value: "indore" },
    { label: "Thane", value: "thane" },
    { label: "Bhopal", value: "bhopal" },
    { label: "Visakhapatnam", value: "visakhapatnam" },
    { label: "Pimpri-Chinchwad", value: "pimpri-chinchwad" },
    { label: "Patna", value: "patna" },
    { label: "Vadodara", value: "vadodara" },
    { label: "Ghaziabad", value: "ghaziabad" },
  ];

  // Check for changes
  useEffect(() => {
    const statesChanged = JSON.stringify(exclusions.excludedStates.sort()) !== JSON.stringify(editExclusions.excludedStates.sort());
    const citiesChanged = JSON.stringify(exclusions.excludedCities.sort()) !== JSON.stringify(editExclusions.excludedCities.sort());
    const pincodesChanged = JSON.stringify(exclusions.excludedPincodes.sort()) !== JSON.stringify(editExclusions.excludedPincodes.sort());
    
    setHasChanges(statesChanged || citiesChanged || pincodesChanged);
  }, [exclusions, editExclusions]);

  // Initialize edit state
  useEffect(() => {
    setEditExclusions(exclusions);
  }, [exclusions]);

  const handleStateChange = (value: string) => {
    const newStates = editExclusions.excludedStates.includes(value)
      ? editExclusions.excludedStates.filter(state => state !== value)
      : [...editExclusions.excludedStates, value];
    
    setEditExclusions(prev => ({ ...prev, excludedStates: newStates }));
  };

  const handleCityChange = (value: string) => {
    const newCities = editExclusions.excludedCities.includes(value)
      ? editExclusions.excludedCities.filter(city => city !== value)
      : [...editExclusions.excludedCities, value];
    
    setEditExclusions(prev => ({ ...prev, excludedCities: newCities }));
  };

  const handlePincodesChange = (pincodes: string[]) => {
    setEditExclusions(prev => ({ ...prev, excludedPincodes: pincodes }));
  };

  const handleSave = () => {
    setExclusions(editExclusions);
    setHasChanges(false);
    // TODO: Save to API
    console.log('Saving delivery exclusions:', editExclusions);
  };

  const handleCancel = () => {
    setEditExclusions(exclusions);
    setHasChanges(false);
  };

  const getSelectedStateLabels = () => {
    return editExclusions.excludedStates.map(stateValue => 
      states.find(state => state.value === stateValue)?.label || stateValue
    ).join(', ');
  };

  const getSelectedCityLabels = () => {
    return editExclusions.excludedCities.map(cityValue => 
      cities.find(city => city.value === cityValue)?.label || cityValue
    ).join(', ');
  };

  return (
    <div className="space-y-2">
      <div className="pl-4 mb-6">
        <h2 className="text-xl font-semibold">Delivery Locations</h2>
        <p className="text-muted-foreground text-sm">Configure regions where delivery is not available</p>
      </div>

      {/* Excluded States */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <MapPin className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <CardTitle>Excluded States</CardTitle>
              <CardDescription>Select states where delivery is not available</CardDescription>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {editExclusions.excludedStates.length} state{editExclusions.excludedStates.length !== 1 ? 's' : ''} excluded
          </div>
        </CardHeader>
        <CardContent>
          <StandardFormSelect
            label="States"
            value=""
            onValueChange={handleStateChange}
            options={states}
            placeholder="Select states to exclude"
          >
            <div className="p-2 border-b border-border bg-secondary-background">
              <h4 className="text-sm font-medium text-primary mb-2">Selected States:</h4>
              <p className="text-xs text-muted-foreground">
                {editExclusions.excludedStates.length > 0 ? getSelectedStateLabels() : "No states excluded"}
              </p>
            </div>
          </StandardFormSelect>
        </CardContent>
      </Card>

      {/* Excluded Cities */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <MapPin className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <CardTitle>Excluded Cities</CardTitle>
              <CardDescription>Select specific cities where delivery is not available</CardDescription>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {editExclusions.excludedCities.length} city{editExclusions.excludedCities.length !== 1 ? 's' : ''} excluded
          </div>
        </CardHeader>
        <CardContent>
          <StandardFormSelect
            label="Cities"
            value=""
            onValueChange={handleCityChange}
            options={cities}
            placeholder="Select cities to exclude"
          >
            <div className="p-2 border-b border-border bg-secondary-background">
              <h4 className="text-sm font-medium text-primary mb-2">Selected Cities:</h4>
              <p className="text-xs text-muted-foreground">
                {editExclusions.excludedCities.length > 0 ? getSelectedCityLabels() : "No cities excluded"}
              </p>
            </div>
          </StandardFormSelect>
        </CardContent>
      </Card>

      {/* Excluded Pincodes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Excluded Pincodes</CardTitle>
              <CardDescription>Enter specific pincodes where delivery is not available</CardDescription>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {editExclusions.excludedPincodes.length} pincode{editExclusions.excludedPincodes.length !== 1 ? 's' : ''} excluded
          </div>
        </CardHeader>
        <CardContent>
          <StandardFormTagInput
            label="Pincodes"
            tags={editExclusions.excludedPincodes}
            onTagsChange={handlePincodesChange}
            placeholder="Enter pincode and press Enter"
            helperText="Add pincodes one by one. Press Enter to add each pincode."
          />
        </CardContent>
      </Card>

      {/* Save/Cancel Buttons */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 bg-background border border-border rounded-lg p-3 shadow-lg">
          <Button
            onClick={handleSave}
            size="sm"
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeliveryLocations;